import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useParams } from 'react-router-dom';
import { useHistory, useLocation } from 'react-router';
import {
  DeprecatedButtonGroup as ButtonGroup,
  HeadingText50,
  HeadingText30,
  BodyTextMedium,
  Modal,
  Icon,
  BodyTextSmall,
} from '@modernhealth/vital-components';
import { FormattedDate, FormattedTime } from 'react-intl';
import { colors } from '@modernhealth/vital-foundation';
import { useAnalytics } from '@modernhealth/web-analytics';

import useShowSkeletonDelay from 'src/shared/hooks/useShowSkeletonDelay';
import NotFoundPage from 'src/modules/404/pages/NotFound';

import { ReactComponent as Confirmation } from '../assets/images/Confirmation.svg';
import { ReactComponent as SocialGoogle } from '../assets/images/social_google.svg';
import { ReactComponent as SocialOutlook } from '../assets/images/social_outlook.svg';
import { useFetchSessionQuery } from '../documents/sessions.generated';
import { useGetCalendarLinksQuery } from '../documents/topic.generated';
import TopicPageSkeleton from './skeletons/TopicPageSkeleton';

interface RsvpProps {
  path?: string;
  sessionId: string;
  rsvpId: string;
  topicSlug: string;
}

interface StateRouterProps {
  email?: string;
}
const PageWrapper = styled.div({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  maxWidth: '32rem',
  margin: '0 auto',
  alignItems: 'center',
});

const StyledCalendarLinkContainer = styled.a({
  width: '100%',
  display: 'inline-flex',
  borderRadius: '128px',
  border: `2px solid ${colors.gray05}`,
  padding: '1rem',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  '& svg': {
    maxWidth: 16,
    maxHeight: 16,
  },
});

const ClampedText = styled(BodyTextMedium)({
  textAlign: 'center',
  WebkitLineClamp: '3',
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
});
const StyledCalendarLink = ({
  href,
  onClick,
  icon,
  children,
}: {
  children: React.ReactNode;
  href: string;
  icon: JSX.Element;
  onClick: () => void;
}) => {
  return (
    <StyledCalendarLinkContainer href={href} target="_blank" rel="noreferrer" onClick={onClick}>
      {icon}
      <BodyTextMedium fontColor="gray55">{children}</BodyTextMedium>
    </StyledCalendarLinkContainer>
  );
};

const RsvpConfirmation: React.FC<unknown> = () => {
  const analytics = useAnalytics();
  const history = useHistory();
  const location = useLocation<StateRouterProps | undefined>();
  const [showModal, setShowModal] = useState(false);
  const { sessionId, rsvpId } = useParams<RsvpProps>();
  const { data, loading } = useFetchSessionQuery({
    variables: {
      sessionId,
    },
  });

  const { data: rsvpData, loading: rsvpLoading } = useGetCalendarLinksQuery({
    variables: {
      rsvpId,
    },
  });

  //TODO: Calendar confirmation Skeleton- Using TopicPageSkeleton for now
  const show = useShowSkeletonDelay(!!data || !!rsvpData);

  if (show || (loading && !data) || (rsvpLoading && !rsvpData)) {
    return <TopicPageSkeleton />;
  }

  if (rsvpData?.circleCalendarLinksByRsvp?.__typename !== 'CircleCalendarLinks')
    return <NotFoundPage />;

  const googleLink = rsvpData.circleCalendarLinksByRsvp.google;
  const outlookLink = rsvpData.circleCalendarLinksByRsvp.outlook;
  const ics = rsvpData.circleCalendarLinksByRsvp.ics;

  const topicName = data?.circleSession?.topic.name;
  const startTime = data?.circleSession?.startTime;
  const duration = data?.circleSession?.topic.duration;

  const { email } = location?.state ?? {};
  return (
    <PageWrapper>
      <Modal
        maxWidth={512}
        isVisible={showModal}
        title="Select calendar"
        onCloseButtonClick={() => {
          setShowModal(false);
        }}
      >
        <div css={{ display: 'flex', flexDirection: 'column', gap: 16, marginTop: 16 }}>
          {googleLink ? (
            <StyledCalendarLink
              href={googleLink}
              icon={<SocialGoogle />}
              onClick={() => {
                analytics.trackClick('Circles: Launched Add to Calendar Deeplink', {
                  calendar: 'google',
                });
              }}
            >
              Add to Google Calendar
            </StyledCalendarLink>
          ) : null}

          {outlookLink ? (
            <StyledCalendarLink
              href={outlookLink}
              icon={<SocialOutlook />}
              onClick={() => {
                analytics.trackClick('Circles: Launched Add to Calendar Deeplink', {
                  calendar: 'outlook',
                });
              }}
            >
              Add to Microsoft Outlook
            </StyledCalendarLink>
          ) : null}

          {ics ? (
            <StyledCalendarLink
              href={`https://api.joinmodernhealth.com${ics}`}
              icon={<Icon color="gray55" name="calendar-date" />}
              onClick={() => {
                analytics.trackClick('Circles: Launched Add to Calendar Deeplink', {
                  calendar: 'ics',
                });
              }}
            >
              Add to calendar (.ics)
            </StyledCalendarLink>
          ) : null}
        </div>
      </Modal>
      <Confirmation height="15rem" width="15rem" css={{ marginBottom: '3rem' }} />
      <HeadingText50>You're in!</HeadingText50>
      <HeadingText30 css={{ marginBottom: '1rem' }}>{topicName}</HeadingText30>
      <BodyTextSmall fontColor="gray55" css={{ marginBottom: '1rem' }}>
        <FormattedDate weekday="short" month="short" day="numeric" value={startTime} />
        {' · '}
        <FormattedTime hour="numeric" minute="numeric" timeZoneName="short" value={startTime} />
        <span>
          {' · '}
          {duration} min
        </span>
      </BodyTextSmall>
      <ClampedText fontColor="gray55">
        We'll send you a confirmation email {email ? `at ${email}.` : 'shortly.'}
      </ClampedText>
      <BodyTextMedium css={{ marginBottom: '32px' }} fontColor="gray55">
        Be sure to keep an eye on your email.
      </BodyTextMedium>
      <ButtonGroup
        firstButtonText="Add to calendar"
        onFirstButtonClick={() => {
          analytics.trackClick('Circles: Open Add to Calendar Modal');

          setShowModal(true);
        }}
        secondButtonText="Continue exploring circles"
        onSecondButtonClick={() => {
          history.push('/');
        }}
      />
    </PageWrapper>
  );
};

export default RsvpConfirmation;
